import { noop } from 'lodash';
import { useRouter } from 'next/router';
import { createContext, memo, type ProviderProps, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type TableHeaderPositionContextType = {
  tableHeaderTopPosition: number;
  setTableHeaderTopPosition: (topPos: number) => void;
};

export const initialTableHeaderPositionContext: TableHeaderPositionContextType = {
  tableHeaderTopPosition: 0,
  setTableHeaderTopPosition: noop,
};

export const TableHeaderPositionContext = createContext<TableHeaderPositionContextType>(
  initialTableHeaderPositionContext,
);

export type TableHeaderPositionContextProviderProps = Pick<ProviderProps<TableHeaderPositionContextType>, 'children'>;

export const TableHeaderPositionContextProvider = memo(({ children }: TableHeaderPositionContextProviderProps) => {
  const router = useRouter();
  const [tableHeaderTopPosition, setTableHeaderTopPosition] = useState(0);

  const value = useMemo(() => ({ tableHeaderTopPosition, setTableHeaderTopPosition }), [tableHeaderTopPosition]);

  const handleResetTableHeaderTopPosition = useCallback((_url: string, { shallow }: { shallow?: boolean } = {}) => {
    // Use routeChangeStart so the reset fires before the new page renders.
    // routeChangeComplete would race with useHeaderResize's ResizeObserver callback,
    // which can set the correct position before the reset overwrites it.
    if (!shallow) {
      setTableHeaderTopPosition(0);
    }
  }, []);

  useEffect(() => {
    router.events.on('routeChangeStart', handleResetTableHeaderTopPosition);

    return () => {
      router.events.off('routeChangeStart', handleResetTableHeaderTopPosition);
    };
  }, [handleResetTableHeaderTopPosition, router.events]);

  return <TableHeaderPositionContext.Provider value={value}>{children}</TableHeaderPositionContext.Provider>;
});

TableHeaderPositionContextProvider.displayName = 'TableHeaderPositionContextProvider';

export const useTableHeaderPositionContext = () => {
  const context = useContext(TableHeaderPositionContext);

  if (context === initialTableHeaderPositionContext) {
    const error = 'useTableHeaderPositionContext must be used within a TableHeaderPositionContextProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
